import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, X, Phone, Calendar, RotateCcw, Ban, Activity, Clock, 
  Briefcase, Wallet, Building, AlertCircle, Edit3, User, ShieldCheck, Trophy, MapPin, Mail 
} from 'lucide-react';
import { formatCurrency, formatDate, getActivityMeta, formatActivityMessage } from '../../utils/helpers';
import { contractsService } from '../../services/api';
import { Skeleton } from '../ui';
import { useLanguage } from '../../contexts/LanguageContext';

export default function ContractDetailsModal({ contract: initialContract, onClose, onExtend, onTerminate }) {
  const { t } = useLanguage();
  const [contract, setContract] = useState(initialContract);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const activityEndRef = useRef(null);

  useEffect(() => {
    const fetchFullDetails = async () => {
      try {
        const res = await contractsService.getById(initialContract.id);
        if (res.status) {
          setContract({ ...initialContract, ...res.data });
        }
      } catch (err) {
        console.error('Failed to fetch contract details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFullDetails();
  }, [initialContract.id]);

  const isTerminated = contract.status === 'Terminated';
  
  const calculateDuration = (start, end) => {
    if (!start || !end) return '—';
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s) || isNaN(e) || s >= e) return '—';
    
    let years = e.getFullYear() - s.getFullYear();
    let months = e.getMonth() - s.getMonth();
    let days = e.getDate() - s.getDate();
    
    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(e.getFullYear(), e.getMonth(), 0);
      days += prevMonth.getDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }
    
    const parts = [];
    if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
    
    return parts.length > 0 ? parts.join(' ') : '0 days';
  };

  const getDaysLeft = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate); expiry.setHours(0, 0, 0, 0);
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };
  const daysLeft = getDaysLeft(contract.endDate || contract.expiryDate);
  const showActionButtons = !isTerminated && (daysLeft === null || daysLeft <= 45);

  // Merge timeline activities
  const timeline = [];

  const processActivity = (a, source) => {
    let data = {};
    let raw = a.content;
    let isProfilePhase = false;
    if (typeof raw === 'string' && raw.startsWith('[Profile Phase] ')) {
      isProfilePhase = true;
      raw = raw.substring('[Profile Phase] '.length);
    }
    try {
      data = JSON.parse(raw);
    } catch (e) {
      data = { note: raw };
    }
    return { ...a, source, data, parsedType: a.type?.toLowerCase() || 'note_added' };
  };

  if (contract.profile?.activities) {
    contract.profile.activities.forEach(a => {
      timeline.push(processActivity(a, 'Profile Phase'));
    });
  }
  if (contract.prequalification?.activities) {
    contract.prequalification.activities.forEach(a => timeline.push(processActivity(a, 'Prequalification')));
  }
  if (contract.tender?.activities) {
    contract.tender.activities.forEach(a => timeline.push(processActivity(a, 'Tender/Proposal')));
  }
  if (contract.activities) {
    contract.activities.forEach(a => timeline.push(processActivity(a, 'Contract')));
  }
  
  timeline.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); // Oldest first to match Tender UI

  useEffect(() => {
    if (activeTab === 'activity') activityEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeTab, timeline]);

  const totalValue = contract.totalValue || contract.contractValue || 0;
  const totalPaid = contract.totalPaid || contract.paidAmount || 0;
  const totalDue = Math.max(0, totalValue - totalPaid);

  const profile = contract.profile;
  const prequal = contract.prequalification;
  const tender = contract.tender;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4 font-sans transition-all" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="bg-white w-full sm:max-w-5xl h-[95vh] sm:h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden relative">
        
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-start gap-3 sticky top-0 z-20">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                {profile?.projectName 
                  ? `${profile.projectName} - ${contract.clientName || profile?.clientName || 'Contract Details'}`
                  : (contract.projectName || contract.title || contract.clientName || profile?.clientName || 'Contract Details')}
              </h2>
              <span className={`px-2.5 py-0.5 text-[10px] sm:text-xs rounded-full border font-bold uppercase tracking-wider whitespace-nowrap ${
                isTerminated ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'
              }`}>
                {contract.status}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
              <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 font-semibold">#{contract.orderNo || contract.id}</span>
              {contract.createdAt && (
                <span className="flex items-center gap-1 font-medium whitespace-nowrap">
                  <Clock className="w-3 h-3" />
                  <span className="text-gray-400 font-normal">Created</span>
                  {new Date(contract.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors shrink-0">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden bg-slate-50">
          {/* Sidebar tabs */}
          <div className="w-full sm:w-56 bg-white border-b sm:border-b-0 sm:border-r border-gray-200 p-2 sm:p-3 flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-x-visible no-scrollbar shrink-0">
            <button onClick={() => setActiveTab('overview')}
              className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${activeTab === 'overview' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'}`}>
              <Edit3 className="w-4 h-4 shrink-0" /> {t('contractOverview') || 'Contract Overview'}
            </button>
            <button onClick={() => setActiveTab('activity')}
              className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${activeTab === 'activity' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'}`}>
              <Activity className="w-4 h-4 shrink-0" /> {t('timeline') || 'Timeline'}
              {timeline.length > 0 && (
                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{timeline.length}</span>
              )}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-8 relative">
            {loading ? (
              <div className="space-y-6 max-w-3xl mx-auto">
                <Skeleton width="40%" height={24} />
                <div className="grid grid-cols-2 gap-4"><Skeleton height={80} /><Skeleton height={80} /></div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto pb-12">
                
                {/* 1. OVERVIEW TAB */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div className="bg-blue-50/40 p-6 rounded-xl border border-blue-200 shadow-sm space-y-4">
                      <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-blue-100 pb-3">
                        <Briefcase className="w-4 h-4 shrink-0 text-blue-500" /> {t('generalDetails') || 'General Details'}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('projectName') || 'Project Name'}</p>
                          <p className="font-semibold text-gray-900">{contract.projectName || contract.title || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('serviceType') || 'Service Type'}</p>
                          <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 inline-block">
                            {contract.serviceType || contract.contractType || '—'}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('services') || 'Services'}</p>
                          <p className="font-medium text-gray-900">
                            {contract.services?.length > 0 
                              ? contract.services.map(s => typeof s === 'object' ? s.name : s).join(', ')
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50/40 p-6 rounded-xl border border-purple-200 shadow-sm space-y-4">
                      <h3 className="text-xs font-bold text-purple-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-purple-100 pb-3">
                        <Calendar className="w-4 h-4 shrink-0 text-purple-500" /> {t('timeline') || 'Timeline'} & Dates
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('enlistedDate') || 'Enlisted Date'}</p>
                          <p className="font-medium text-gray-900">{contract.prequalification?.enlistedDate ? formatDate(contract.prequalification.enlistedDate) : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('startDate') || 'Start Date'}</p>
                          <p className="font-medium text-gray-900">{(contract.startDate || contract.createdAt) ? formatDate(contract.startDate || contract.createdAt) : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('expiryDate') || 'Expiry Date'}</p>
                          <p className="font-medium text-gray-900">{(contract.endDate || contract.expiryDate) ? formatDate(contract.endDate || contract.expiryDate) : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('duration') || 'Duration'}</p>
                          <p className="font-medium text-gray-900">{calculateDuration(contract.startDate || contract.createdAt, contract.endDate || contract.expiryDate)}</p>
                        </div>
                      </div>
                    </div>
                {/* 2. CLIENT TAB CONTENT IN OVERVIEW */}
                    <div className="bg-orange-50/40 p-6 rounded-xl border border-orange-200 shadow-sm space-y-6">
                      <h3 className="text-xs font-bold text-orange-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-orange-100 pb-3">
                        <Building className="w-4 h-4 shrink-0 text-orange-500" /> {t('clientProfileDetails') || 'Client Profile Details'}
                      </h3>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('clientName') || 'Client Name'}</p>
                          <p className="font-semibold text-gray-900">{profile?.clientName || contract.clientName || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('contactPersonName') || 'Contact Person Name'}</p>
                          <p className="font-semibold text-gray-900">{profile?.contactPersonName || '—'}</p>
                        </div>
                        
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('designation') || 'Designation'}</p>
                          <p className="font-medium text-gray-900">{profile?.designation || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('contactDetailExtension') || 'Contact Detail / Extension'}</p>
                          <p className="font-medium text-gray-900">{profile?.contactDetailExt || '—'}</p>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('email') || 'Email'}</p>
                          <p className="font-medium text-gray-900 break-all flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            {profile?.email || '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('mobile') || 'Mobile'}</p>
                          <p className="font-medium text-gray-900 flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            {profile?.mobile || contract.phone || '—'}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('address') || 'Address'}</p>
                          <p className="font-medium text-gray-900">{profile?.address || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('city') || 'City'}</p>
                          <p className="font-medium text-gray-900">{profile?.city || '—'}</p>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('projectName') || 'Project Name'}</p>
                          <p className="font-medium text-gray-900">{profile?.projectName || contract.projectName || contract.title || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('date') || 'Date'}</p>
                          <p className="font-medium text-gray-900">{profile?.date ? formatDate(profile.date) : '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase">{t('source') || 'Source'}</p>
                          <p className="font-medium text-gray-900">{profile?.source?.name || profile?.source || contract.source?.name || '-'}</p>
                        </div>
                      </div>
                    </div>
                {/* 3. PREQUALIFICATION TAB CONTENT IN OVERVIEW */}
                {prequal && (
                    <div className="bg-emerald-50/30 p-6 rounded-xl border border-emerald-200 shadow-sm space-y-6">
                      <div className="flex justify-between items-center border-b border-emerald-200 pb-3">
                        <h3 className="text-xs font-bold text-emerald-700 uppercase tracking-widest flex items-center gap-1.5">
                          <Clock className="w-4 h-4 shrink-0" /> {t('prequalificationHistory') || 'Prequalification History'}
                        </h3>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('createdOn') || 'Created On'}</p>
                          <p className="font-semibold text-gray-900">{prequal.createdAt ? formatDate(prequal.createdAt) : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('submissionDate') || 'Submission Date'}</p>
                          <p className="font-semibold text-gray-900">{prequal.submissionDate ? formatDate(prequal.submissionDate) : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('enlistedOn') || 'Enlisted On'}</p>
                          <p className="font-semibold text-gray-900">{prequal.enlistedDate ? formatDate(prequal.enlistedDate) : '—'}</p>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('assignee') || 'Assignee'}</p>
                          <p className="font-medium text-gray-900">{prequal.assignee?.username || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('source') || 'Source'}</p>
                          <p className="font-medium text-gray-900">{prequal.source?.name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('documentStatus') || 'Document Status'}</p>
                          <p className="font-medium text-gray-900">{prequal.documentStatus || '—'}</p>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('earnestMoney') || 'Earnest Money'}</p>
                          <p className="font-medium text-gray-900">{prequal.earnestMoneyRequired ? `Required (${prequal.earnestMoneyValue || 0})` : 'Not Required'}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('attachmentUrl') || 'Attachment Url'}</p>
                          <p className="font-medium">
                            {prequal.attachmentUrl ? (
                              <a href={prequal.attachmentUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">View Document</a>
                            ) : '—'}
                          </p>
                        </div>

                        <div className="sm:col-span-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('services') || 'Services'}</p>
                          <p className="font-medium text-gray-900">
                            {prequal.services?.length > 0 
                              ? prequal.services.map(s => s.name || s).join(', ')
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                )}
                {/* 4. TENDER TAB CONTENT IN OVERVIEW */}
                {tender && (
                  <div className="bg-rose-50/30 p-6 rounded-xl border border-rose-200 shadow-sm space-y-8">
                    
                    {/* TENDER DETAILS */}
                    <div className="space-y-5">
                      <h3 className="text-xs font-bold text-rose-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-rose-100 pb-3">
                        <Edit3 className="w-4 h-4 shrink-0 text-rose-500" /> {t('tenderDetails') || 'Tender/Proposal Details'}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('title') || 'Title'}</p>
                          <p className="font-semibold text-gray-900">{tender.title || tender.projectName || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('type') || 'Type'}</p>
                          <p className="font-medium text-gray-900">{tender.type === 'PROPOSAL' ? 'Proposal' : 'Tender'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('assignee') || 'Assignee'}</p>
                          <p className="font-medium text-gray-900">{tender.assignee?.username || '—'}</p>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('proposalValue') || 'Proposal Value'}</p>
                          <p className="font-bold text-gray-900">{formatCurrency(tender.proposalValue || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('negotiationPKR') || 'Negotiated Amount (PKR)'}</p>
                          <p className="font-medium text-gray-900">{formatCurrency(tender.negotiation || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('grossProfit') || 'Gross Profit'}</p>
                          <p className="font-medium text-gray-900">{formatCurrency(tender.grossProfit || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('gpPercent') || 'GP %'}</p>
                          <p className="font-bold text-emerald-600">{tender.gpPercent || 0}%</p>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('wht') || 'WHT'}</p>
                          <p className="font-medium text-gray-900">{tender.wht || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('mobilizationAdvance') || 'Mobilization Advance'}</p>
                          <p className="font-medium text-gray-900">{tender.mobilizationAdvance ? formatCurrency(tender.mobilizationAdvance) : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">Contract End Date / Duration</p>
                          <p className="font-medium text-gray-900">{tender.contractDuration || '—'}</p>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">No of Staff</p>
                          <p className="font-medium text-gray-900">{tender.noOfStaff || '0'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">Earnest Money Value</p>
                          <p className="font-medium text-gray-900">{tender.earnestMoneyValue ? formatCurrency(tender.earnestMoneyValue) : '—'}</p>
                        </div>
                        
                        <div className="sm:col-span-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('paymentTerms') || 'Payment Terms'}</p>
                          <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                            {tender.paymentTerms || '—'}
                          </div>
                        </div>

                        <div className="sm:col-span-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('winningStrategy') || 'Winning Strategy'}</p>
                          <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-100">
                            {tender.winningStrategy || '—'}
                          </div>
                        </div>
                        
                        <div className="sm:col-span-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1 uppercase">{t('notes') || 'Notes'}</p>
                          <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-100">
                            {tender.notes || '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                  </div>
                )}

                {/* 5. TIMELINE TAB */}
                {activeTab === 'activity' && (
                  <div className="flex flex-col h-full bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    {timeline.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No activity yet</p>
                      </div>
                    ) : (
                      <div className="space-y-4 relative border-l-2 border-gray-100 ml-4 pl-6 pb-2">
                        {timeline.map((item, i) => {
                          const isPQ = item.source === 'Prequalification';
                          const isTender = item.source === 'Tender/Proposal';
                          const meta = getActivityMeta(item.parsedType);
                          
                          // override color to denote source if we want
                          const iconBg = isPQ ? 'bg-emerald-100 text-emerald-600' : isTender ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600';
                          const badgeClass = isPQ ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : isTender ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200';

                          return (
                            <div key={i} className="relative">
                              <div className={`absolute -left-[41px] w-8 h-8 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0 ring-4 ring-white shadow-sm`}>
                                <span className="text-sm">{meta.icon}</span>
                              </div>
                              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-1 gap-2">
                                  <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                                    {formatActivityMessage({ type: item.parsedType, ...item.data })}
                                  </p>
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider whitespace-nowrap border shrink-0 ${badgeClass}`}>
                                    {item.source}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 font-medium">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3.5 h-3.5" />
                                    {new Date(item.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  {item.user && (
                                    <>
                                      <span>•</span>
                                      <span className="text-gray-700 flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        {item.user.username}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={activityEndRef} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-4 sm:px-6 py-4 bg-white shrink-0 flex justify-between items-center z-20">
          <div className="flex gap-3">
            {showActionButtons ? (
              <>
                <button
                  onClick={onExtend}
                  className="h-[40px] px-5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-sm"
                >
                  <RotateCcw className="w-4 h-4" />
                  Extend
                </button>
                <button
                  onClick={onTerminate}
                  className="h-[40px] px-5 bg-white border border-red-200 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-50 transition-colors flex items-center justify-center gap-2 shadow-sm"
                >
                  <Ban className="w-4 h-4" />
                  Terminate
                </button>
              </>
            ) : (
              <span className="px-3 py-1.5 rounded-lg text-sm font-bold bg-red-100 text-red-700">Terminated</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="h-[40px] px-6 border border-gray-200 bg-white text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>

      </motion.div>
    </motion.div>
  );
}
